<template>
  <model-form
    ref="modelForm"
    :model="movie"
  >
    <div class="q-gutter-sm">
      <div class="row q-col-gutter-md">
        <model-form-input
          field="name"
          class="col-3"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
          autofocus
        />
        <model-form-input
          field="urlSlug"
          class="col-3"
          label="Url Slug"
          debounce="100"
          :readonly="movie.urlSlugIsReadonly"
          :rules="[
            urlSlugAvailable,
            val => /^[a-z0-9\-]*$/.test(val) || 'Restricted to lowercase letters, numbers, and hyphens'
          ]"
          lazy-rules
        >
          <q-tooltip>
            Used to identify this movie in links.
          </q-tooltip>
        </model-form-input>
        <div class="col-3 q-py-none q-my-auto">
          <div
            class="q-pa-sm text-body2"
            style="border: solid 2px; height: 40px; border-radius: 4px; cursor: pointer;"
            :style="'color: ' + movie.color + '; border-color: ' + movie.color + '; background-color: ' + movie.color + '33;'"
          >
            <div class="ellipsis">
              {{ movie.name }}
            </div>
            <q-menu
              anchor="top left"
              self="top left"
            >
              <q-color
                v-model="movie.color"
                :disable="movie.isTranslation"
              >
                <untranslatable-field-tooltip v-if="movie.isTranslation" />
              </q-color>
            </q-menu>
          </div>
        </div>
        <div class="col-3 q-py-none q-my-auto">
          <tmdb-sync-dropdown
            style="height: 40px; width: 100%;"
            :movie="movie"
            @sync="$refs.modelForm.triggerValidate()"
          />
        </div>
      </div>
      <div class="row q-col-gutter-md justify-between">
        <model-form-checkbox
          field="includeInComingSoon"
          class="col-2 q-pt-md q-my-auto"
          label="Coming Soon"
          :disable="movie.willIncludeInComingSoon && !movie.includeInComingSoon"
        />
        <q-tooltip v-if="movie.future && movie.willIncludeInComingSoon && !movie.includeInComingSoon">
          Will be included in coming soon because there is a published showing for this movie. To hide from the public, set the showing to private.
        </q-tooltip>
        <div class="col-3 q-py-none q-my-auto">
          <q-btn
            class="q-pa-sm"
            style="height: 40px; width: 100%;"
            label="Terms"
            icon-right="launch"
            dense
            flat
            @click="$router.push(`/settings/programming/movie-terms/${movie.id}`)"
          />
        </div>
      </div>
      <div class="row text-h5 q-mt-lg toggle-title">
        <div>
          Poster
          <q-icon
            name="info"
            size="md"
          >
            <q-tooltip>
              Poster Image associated with this movie. This will be displayed on the consumer site, point of sale and signage.
            </q-tooltip>
          </q-icon>
        </div>
        <model-form-toggle
          field="signageDisplayPoster"
          class="text-subtitle1"
          label="Display On Signage"
        >
          <q-tooltip>
            When enabled this will be included in the signage media type signs. With this disabled it will still appear in other signage types.
          </q-tooltip>
        </model-form-toggle>
      </div>
      <div class="row items-start q-col-gutter-lg">
        <model-form-image-uploader
          field="posterImage"
          class="col-4"
          :image-url="movie.posterImageUrl"
        />
      </div>
      <div class="row q-mb-lg">
        <div
          v-for="(color, index) in paletteColors"
          :key="color"
          class="q-ma-xs text-center cursor-pointer"
          style="border: solid 2px; width: 30px; height: 30px; border-radius: 4px; padding-top: 3px;"
          :style="'color: ' + color + '; border-color: ' + color + '; background-color: ' + color + '33;'"
          @click="movie.color = color"
        >
          {{ index + 1 }}
        </div>
      </div>
      <div class="row text-h5 q-mt-lg toggle-title">
        <div>
          Banner
          <q-icon
            name="info"
            size="md"
          >
            <q-tooltip>
              Banner / Landscape Image associated with this movie. This will be displayed on the consumer site, point of sale and optionally signage.
            </q-tooltip>
          </q-icon>
        </div>
        <model-form-toggle
          field="signageDisplayBanner"
          class="text-subtitle1"
          label="Display On Signage"
        >
          <q-tooltip>
            When enabled this will be included in the signage media type signs. With this disabled it will still appear in other signage types.
          </q-tooltip>
        </model-form-toggle>
      </div>
      <div class="row items-start q-col-gutter-lg">
        <model-form-image-uploader
          field="bannerImage"
          class="col-12"
          :image-url="movie.bannerImageUrl"
        />
      </div>
      <div class="row text-h5 q-mt-lg toggle-title">
        <div>
          Animated Poster Video
          <q-icon
            name="info"
            size="md"
          >
            <q-tooltip>
              Short Video / Animated poster associated with this film, used for Screen and Media signage types.
            </q-tooltip>
          </q-icon>
        </div>
        <model-form-toggle
          field="signageDisplayAnimatedPoster"
          class="text-subtitle1"
          label="Display On Signage"
        >
          <q-tooltip>
            When enabled this will be included in the signage media type signs. With this disabled it will still appear in the screen signage type.
          </q-tooltip>
        </model-form-toggle>
      </div>
      <div class="row items-start q-col-gutter-lg">
        <video-uploader
          v-model="movie.animatedPosterVideo"
          class="col-4"
          video-url-property="animatedPosterVideoUrl"
          :model="movie"
        />
      </div>
      <div class="row text-h5 q-mt-lg toggle-title">
        <div>
          Trailer Video
          <q-icon
            name="info"
            size="md"
          >
            <q-tooltip>
              Trailer Video associated with this movie, this is used and displayed on signage media displays.
            </q-tooltip>
          </q-icon>
        </div>
        <model-form-toggle
          field="signageDisplayTrailer"
          class="text-subtitle1"
          label="Display On Signage"
        >
          <q-tooltip>
            When enabled this will be included in the signage media type signs.
          </q-tooltip>
        </model-form-toggle>
      </div>
      <div class="row items-start q-col-gutter-lg">
        <video-uploader
          v-model="movie.trailerVideo"
          class="col-4"
          video-url-property="trailerVideoUrl"
          :model="movie"
        />
      </div>
      <div class="row q-mb-lg">
        <div
          v-for="(color, index) in paletteColors2"
          :key="color"
          class="q-ma-xs text-center cursor-pointer"
          style="border: solid 2px; width: 30px; height: 30px; border-radius: 4px; padding-top: 3px;"
          :style="'color: ' + color + '; border-color: ' + color + '; background-color: ' + color + '33;'"
          @click="movie.color = color"
        >
          {{ index + 1 }}
        </div>
      </div>
      <div class="row q-col-gutter-sm q-mt-lg">
        <model-form-input
          field="signageMessageOverride"
          class="col-6"
          label="Signage Message Override"
        >
          <q-tooltip>
            Overrides the default "Now Playing" or "Coming Soon" text on media signage.
          </q-tooltip>
        </model-form-input>
      </div>
      <div class="row q-col-gutter-sm q-mt-lg">
        <model-form-input
          field="synopsis"
          class="col-12"
          type="textarea"
          label="Synopsis"
        />
      </div>
      <div class="row q-col-gutter-sm q-mt-lg">
        <model-form-input
          field="starring"
          class="col-12"
          type="textarea"
          label="Starring"
        />
      </div>
      <div class="row q-col-gutter-sm q-mt-lg">
        <model-form-input
          field="directedBy"
          class="col-12"
          type="textarea"
          label="Directed By"
        />
      </div>
      <div class="row q-col-gutter-sm q-mt-lg">
        <model-form-input
          field="producedBy"
          class="col-12"
          type="textarea"
          label="Produced By"
        />
      </div>
      <div class="row q-col-gutter-sm q-mt-lg">
        <model-form-input
          field="searchTerms"
          class="col-12"
          type="textarea"
          label="Search Terms (space separated list of words used for finding a movie when searching in Admin or by moviegoers, e.g. 'animated kids family trilogy')"
        />
      </div>
      <div class="row q-col-gutter-sm q-mt-lg items-center">
        <model-form-toggle
          field="isMarathon"
          class="col-2"
          label="Marathon"
        />
        <div class="col-10">
          <q-select
            v-if="movie.isMarathon"
            v-model="movie.marathonMovies.data"
            label="Marathon Movies"
            :options="movies.data"
            option-label="name"
            outlined
            multiple
            clearable
            :use-input="true"
            input-style="search"
            input-debounce="100"
            stack-label
            :rules="[validateMarathonMovies]"
            :readonly="movie.isTranslation"
            @filter="filterMovies"
          >
            <template #selected-item="scope">
              <q-chip
                v-if="scope.opt.name"
                :removable="!movie.isTranslation"
                dense
                :style="'color: ' + scope.opt.color + '; border-color: ' + scope.opt.color + '; background-color: ' + scope.opt.color + '33;'"
                :tabindex="scope.tabindex"
                :label="scope.opt.name"
                @remove="scope.removeAtIndex(scope.index)"
              />
            </template>
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
                v-on="scope.itemEvents"
              >
                <q-item-section>
                  {{ scope.opt.name }}
                </q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>
      </div>
      <div class="row q-col-gutter-sm q-mt-lg items-center">
        <model-form-input
          field="releaseDate"
          class="col-4"
          label="Original Release Date"
        />
        <model-form-input
          field="dateOfFirstShowing"
          class="col-4"
          label="Date of First Showing"
          :disable="!movie.overrideDateOfFirstShowing"
        />
        <model-form-toggle
          field="overrideDateOfFirstShowing"
          class="col-2 q-mb-md"
          label="Override"
          @update:model-value="toggleOverrideDateOfFirstShowing"
        />
        <model-form-checkbox
          field="hideDateOfFirstShowing"
          class="col-2 q-mb-md"
          label="Hide on Movie Page"
        />
      </div>
      <div class="row q-col-gutter-sm items-center">
        <q-input
          class="col-5"
          :model-value="date.formatDate(displayEmbargoShowingLiftedAt, 'YYYY MMM D, h:mm a')"
          label="Showing Embargo Lifts At"
          :readonly="movie.isTranslation"
          clearable
          @clear="movie.embargoShowingLiftedAt = null"
        >
          <template #prepend>
            <q-icon
              name="event"
              class="cursor-pointer"
            >
              <q-popup-proxy
                transition-show="scale"
                transition-hide="scale"
              >
                <q-date
                  :model-value="date.formatDate(displayEmbargoShowingLiftedAt, 'YYYY-MM-DDTHH:mm:ssZ')"
                  flat
                  mask="YYYY-MM-DDTHH:mm:ssZ"
                  :readonly="movie.isTranslation"
                  @update:model-value="value => setEmbargoShowingLiftedAt(value)"
                />
              </q-popup-proxy>
            </q-icon>
          </template>
          <template #append>
            <q-icon
              name="access_time"
              class="cursor-pointer"
            >
              <q-popup-proxy
                transition-show="scale"
                transition-hide="scale"
              >
                <q-time
                  :model-value="date.formatDate(displayEmbargoShowingLiftedAt, 'YYYY-MM-DDTHH:mm:ssZ')"
                  flat
                  mask="YYYY-MM-DDTHH:mm:ssZ"
                  :readonly="movie.isTranslation"
                  @update:model-value="value => setEmbargoShowingLiftedAt(value)"
                />
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>
        <q-input
          class="col-5"
          :model-value="date.formatDate(displayEmbargoPurchaseLiftedAt, 'YYYY MMM D, h:mm a')"
          label="Showing Purchase Embargo Lifts At"
          :readonly="movie.isTranslation"
          clearable
          @clear="movie.embargoPurchaseLiftedAt = null"
        >
          <template #prepend>
            <q-icon
              name="event"
              class="cursor-pointer"
            >
              <q-popup-proxy
                transition-show="scale"
                transition-hide="scale"
              >
                <q-date
                  :model-value="date.formatDate(displayEmbargoPurchaseLiftedAt, 'YYYY-MM-DDTHH:mm:ssZ')"
                  flat
                  mask="YYYY-MM-DDTHH:mm:ssZ"
                  :readonly="movie.isTranslation"
                  @update:model-value="value => setEmbargoPurchaseLiftedAt(value)"
                />
              </q-popup-proxy>
            </q-icon>
          </template>
          <template #append>
            <q-icon
              name="access_time"
              class="cursor-pointer"
            >
              <q-popup-proxy
                transition-show="scale"
                transition-hide="scale"
              >
                <q-time
                  :model-value="date.formatDate(displayEmbargoPurchaseLiftedAt, 'YYYY-MM-DDTHH:mm:ssZ')"
                  flat
                  mask="YYYY-MM-DDTHH:mm:ssZ"
                  :readonly="movie.isTranslation"
                  @update:model-value="value => setEmbargoPurchaseLiftedAt(value)"
                />
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>
      </div>
      <div class="row q-col-gutter-sm q-mt-lg">
        <model-form-input
          field="duration"
          class="col"
          label="Duration (minutes)"
          type="number"
          min="0"
          max="600"
          :placeholder="movie.isMarathon && movie.marathonMovies.data.length > 0 ? $_.sumBy(movie.marathonMovies.data, 'duration') : ''"
          :rules="[val => !!val || 'Field is required']"
        />
        <model-form-input
          field="rating"
          class="col"
          label="Rating"
          :rules="[val => !currentSite.sendDcm || !!val || 'Field is required']"
        />
        <model-form-input
          field="ratingReason"
          class="col"
          label="Rating Reason Message"
        />
        <model-form-input
          field="genre"
          class="col"
          label="Genre"
        />
        <model-form-input
          field="allGenres"
          class="col"
          label="All Genres"
        />
      </div>
      <div class="row q-col-gutter-md q-mt-md items-start">
        <model-form-select
          field="titleClass"
          class="col-3"
          label="Title Class"
          :options="titleClasses.data"
          option-label="name"
          :display-value="movie.titleClass && movie.titleClass.name"
          :rules="[val => val !== null || titleClasses.data.length === 0 || 'Field is required']"
        />
        <model-form-select
          field="studio"
          class="col-3"
          label="Studio"
          :options="studios.data"
          option-label="name"
          :display-value="movie.studio && movie.studio.name"
          :rules="[val => val !== null || studios.data.length === 0 || 'Field is required']"
        />
        <showing-badge-select
          :model="movie"
          class="col-3"
          :readonly="movie.isTranslation"
        />
      </div>
      <div class="row q-col-gutter-md q-mt-md items-center">
        <model-form-toggle
          field="overridePriceCard"
          class="col-3 q-pb-md"
          label="Custom Price Card"
        />
        <model-form-select
          field="customPriceCard"
          class="col-3"
          :disable="!movie.overridePriceCard"
          label="Price Card"
          :options="$_.filter(priceCards.data, priceCard => !priceCard.isArchived)"
          option-label="name"
          :display-value="movie.overridePriceCard ? movie.customPriceCard && movie.customPriceCard.name : ''"
        />
        <model-form-toggle
          field="overridePostStartTimeBufferMinutes"
          class="col-3 q-pb-md"
          label="Custom Post-Start Time Buffer (Minutes)"
        />
        <model-form-input
          field="customPostStartTimeBufferMinutes"
          class="col-3"
          label="Post-Start Time Buffer (Minutes)"
          :disable="!movie.overridePostStartTimeBufferMinutes"
          type="number"
          min="0"
          max="100"
          :rules="[val => !!val || val === 0 || 'Field is required']"
        />
      </div>
      <div
        v-if="!movie.isMarathon"
        class="row q-col-gutter-md q-mt-md items-center"
      >
        <model-form-toggle
          field="sendRentrak"
          class="col-5 q-pb-md"
          label="Include in Box Office Reports (Comscore, Numero, etc.)"
        >
          <q-tooltip>
            Send Box Office data to third party eBORs, such as Rentrak Flash, Maccsbox, and Numero.
          </q-tooltip>
        </model-form-toggle>
        <q-icon
          class="col-1 q-pb-md"
          :name="movie.willSendRentrak ? 'check' : 'remove'"
          size="md"
          :color="movie.willSendRentrak ? 'positive' : 'negative'"
        >
          <q-tooltip v-if="movie.willSendRentrak">
            Will send Box Office data to third party eBORs, including Rentrak Flash, Maccsbox, and Numero.
          </q-tooltip>
          <q-tooltip v-else-if="movie.sendRentrak">
            Include Box Office Reports on the Studio to include this Movie in third party eBORs.
          </q-tooltip>
          <q-tooltip v-else>
            Will not send Box Office data to third party eBORs.
          </q-tooltip>
        </q-icon>
        <model-form-input
          field="rentrakName"
          class="col"
          label="Custom Reporting Name"
        >
          <q-tooltip>
            If blank, will use movie name
          </q-tooltip>
        </model-form-input>
      </div>
      <div
        v-if="currentCircuit.iso31661 === 'GB'"
        class="row q-col-gutter-md q-mt-md items-start"
      >
        <model-form-input
          field="dcmEdiMovieId"
          class="col-3"
          label="EDI Movie Identifier"
        >
          <q-tooltip>
            EDI identifier used in box office reports. Get the movie identifier from https://exhibitors.dcm.co.uk/.
          </q-tooltip>
        </model-form-input>
        <model-form-input
          field="dcmEdiMovieName"
          class="col-3"
          label="EDI Movie Name"
          readonly
        >
          <q-tooltip>
            This name corresponds to the EDI Movie Identifier set above.
          </q-tooltip>
        </model-form-input>
      </div>
      <div class="row q-mt-lg">
        <model-form-input
          field="trailerYoutubeId"
          class="col"
          label="YouTube Trailer ID"
        />
      </div>
      <div class="row">
        <div class="col">
          <q-video
            style="width: 100%"
            :ratio="16/9"
            :src="'https://www.youtube.com/embed/' + movie.trailerYoutubeId"
          />
        </div>
      </div>
      <div class="row q-col-gutter-md q-mt-md items-start">
        <model-form-toggle
          field="allowPastSales"
          class="col-3"
          label="Enable sales after showing has started"
        >
          <q-tooltip>This allows sales to this movie for showings that have already started</q-tooltip>
        </model-form-toggle>
        <model-form-toggle
          field="allowPrivateSalesOnExternal"
          class="col-3"
          label="Enable private sales on external clients"
        >
          <q-tooltip>This allows sales to this movie for private showings on external ticketing clients (Fandango, Atom, etc)</q-tooltip>
        </model-form-toggle>
        <model-form-toggle
          field="disableOnlineConcessions"
          class="col-3"
          label="Disable online concessions"
        >
          <q-tooltip>Disable online concessions for this film.</q-tooltip>
        </model-form-toggle>
        <q-select
          v-if="currentSite.checkoutOptions.show_more_films_during_checkout"
          v-model="movie.recommendedMovies.data"
          class="col-6"
          label="Recommended Movies"
          :options="movies.data"
          option-label="name"
          outlined
          multiple
          use-chips
          clearable
          :use-input="true"
          input-style="search"
          input-debounce="100"
          stack-label
          :rules="[val => val.length <= 4 || 'Maximum of 4 recommended movies']"
          @focus="moreFilmsSearch = true"
          @blur="moreFilmsSearch = false"
          @filter="filterMovies"
        />
      </div>
      <div class="row q-col-gutter-md q-mt-md items-start">
        <model-form-toggle
          field="overrideMaxTicketsPerOrderPerShowing"
          class="col-3 q-pb-md"
          label="Override Max Tickets Per Order Per Showing"
        />
        <model-form-input
          field="maxTicketsPerOrderPerShowing"
          class="col-3"
          label="Max Tickets Per Order Per Showing"
          :disable="!movie.overrideMaxTicketsPerOrderPerShowing"
          type="number"
          min="0"
          step="1"
          :rules="[val => !!val || 'Field is required']"
        />
      </div>
      <div
        class="q-ma-sm"
      >
        <q-banner
          class="q-pa-md bg-warning"
        >
          <q-btn
            class="full-width text-h7 text-left justify-start"
            label="Go to Terms"
            icon-right="launch"
            @click="$router.push(`/settings/programming/movie-terms/${movie.id}`)"
          />
          <div class="text-body2">
            Terms have been moved to a new form. To edit the terms for this movie, please click the button above.
            <br>
            Movie Terms can also be found in Settings > Studio Reporting > Movie Terms, for admins with Studio Reporting access.
          </div>
        </q-banner>
      </div>
    </div>
  </model-form>
</template>

<script>
import ColorThief from 'colorthief'
import { date } from 'quasar'
import { IsoDateTime } from 'src/models/model'
import { MOVIE_QUERY_TYPE } from 'src/models/movie/Movie'
import UntranslatableFieldTooltip from 'src/modules/admin/components/ModelForm/components/UntranslatableFieldTooltip'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import TmdbSyncDropdown from 'src/modules/admin/components/TmdbSyncDropdown'
import VideoUploader from 'src/modules/admin/components/VideoUploader'
import ShowingBadgeSelect from 'src/modules/admin/showing-badge/components/ShowingBadgeSelect'

function rgbToHex (rgb) {
  return '#' + rgb.map(x => {
    const hex = x.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }).join('')
}

export default {
  name: 'MovieForm',
  components: {
    ModelForm,
    ShowingBadgeSelect,
    VideoUploader,
    UntranslatableFieldTooltip,
    TmdbSyncDropdown
  },
  configuration: ['priceCards', 'showingBadges', 'titleClasses', 'currentSite', 'currentCircuit'],
  data () {
    return {
      paletteColors: [],
      paletteColors2: [],
      syncingWithTmdb: false,
      movieSearchString: '',
      moreFilmsSearch: false
    }
  },
  models: {
    movie: {
      customPriceCard: true,
      showingBadges: true,
      studio: 'default',
      marathonMovies: 'default',
      recommendedMovies: {
        fields: ['id', 'name']
      },
      willIncludeInComingSoon: true,
      libraryChildSiteIds: true,
      urlSlugIsReadonly: true
    },
    findMovieBySlug: {
      id: true,
      variables () {
        return {
          urlSlug: this.movie.urlSlug || '',
          skip: !this.movie.urlSlug
        }
      }
    },
    studios: {
      variables: {
        limit: 1000
      }
    },
    movies: {
      variables () {
        return {
          isMarathon: this.moreFilmsSearch ? null : false,
          searchString: this.movieSearchString,
          orderBy: 'createdAt',
          descending: true,
          type: this.moreFilmsSearch ? MOVIE_QUERY_TYPE.NOW_PLAYING_WITH_SHOWINGS : null,
          limit: 5,
          skip: !this.movie?.isMarathon && !this.moreFilmsSearch
        }
      }
    }
  },
  computed: {
    displayEmbargoPurchaseLiftedAt () {
      // We want to display the purchase embargo showing lifted at in the current site's timezone, not the system timezone
      if (!this.movie.embargoPurchaseLiftedAt) return null
      if (this.currentSite.timeZone) {
        return IsoDateTime.parse(this.movie.embargoPurchaseLiftedAt).inTimeZone(this.currentSite.timeZone)
      }
      return this.movie.embargoPurchaseLiftedAt
    },
    displayEmbargoShowingLiftedAt () {
      // We want to display the embargo showing lifted at in the current site's timezone, not the system timezone
      if (!this.movie.embargoShowingLiftedAt) return null
      if (this.currentSite.timeZone) {
        return IsoDateTime.parse(this.movie.embargoShowingLiftedAt).inTimeZone(this.currentSite.timeZone)
      }
      return this.movie.embargoShowingLiftedAt
    }
  },
  watch: {
    'movie.posterImageUrl' () {
      if (this.movie.isRemote) {
        this.paletteColors = []
        setTimeout(() => {
          this.generatePalette()
        }, 1000)
      } else {
        this.paletteColors = []
      }
    },
    'movie.bannerImageUrl' () {
      if (this.movie.isRemote) {
        this.paletteColors2 = []
        setTimeout(() => {
          this.generatePalette()
          this.generatePalette2()
        }, 1000)
      } else {
        this.paletteColors2 = []
      }
    }
  },
  created () {
    _.extend(this, { date, MOVIE_QUERY_TYPE })
  },
  methods: {
    show (model, initialData) {
      this.paletteColors = []
      this.paletteColors2 = []

      this.$refs.modelForm.show(model, initialData)
    },
    generatePalette: async function () {
      if (!this.movie.posterImageUrl || !this.$refs.posterImg) {
        return
      }
      const colorThief = new ColorThief()
      const paletteColorRgbs = await colorThief.getPalette(this.$refs.posterImg, 6)
      this.paletteColors = _.map(paletteColorRgbs, rgb => rgbToHex(rgb))
    },
    generatePalette2: async function () {
      if (!this.movie.bannerImageUrl || !this.$refs.bannerImg) {
        return
      }
      const colorThief = new ColorThief()
      const paletteColorRgbs = await colorThief.getPalette(this.$refs.bannerImg, 20)
      this.paletteColors2 = _.map(paletteColorRgbs, rgb => rgbToHex(rgb))
    },
    async urlSlugAvailable () {
      // We don't want to do this check if there is no slug
      if (!this.movie.urlSlug) return true

      await this.findMovieBySlug.fetch()
      return this.findMovieBySlug.isLocal || this.findMovieBySlug.id === this.movie.id || 'Url Slug is already in use'
    },
    toggleOverrideDateOfFirstShowing (value) {
      this.movie.dateOfFirstShowing ||= value && this.movie.releaseDate
    },
    filterMovies (val, update, abort) {
      update(() => {
        this.movieSearchString = val
      })
    },
    setEmbargoPurchaseLiftedAt (dateTimeString) {
      this.movie.embargoPurchaseLiftedAt = this.convertToSiteTimeZone(dateTimeString)
    },
    setEmbargoShowingLiftedAt (dateTimeString) {
      this.movie.embargoShowingLiftedAt = this.convertToSiteTimeZone(dateTimeString)
    },
    convertToSiteTimeZone (dateTimeString) {
      // Remove the system timezone offset and replace with site's timezone offset
      const siteTimeZone = this.currentSite.timeZone
      if (!siteTimeZone || dateTimeString.length !== 25) {
        return date.extractDate(dateTimeString, 'YYYY-MM-DDTHH:mm:ssZ')
      }
      const withoutTimeZone = dateTimeString.slice(0, -6)
      const inSiteTimeZone = withoutTimeZone + this.getTimeZoneOffset(siteTimeZone)
      return date.extractDate(inSiteTimeZone, 'YYYY-MM-DDTHH:mm:ssZ')
    },
    getTimeZoneOffset (timeZone, date = new Date()) {
      // Get the offset in minutes and format like '+HH:00'
      const diffMinutes = this.getOffsetMinutes(timeZone, date)
      const sign = diffMinutes >= 0 ? '+' : '-'
      const hours = Math.floor(Math.abs(diffMinutes) / 60)
        .toString()
        .padStart(2, '0')

      return `${sign}${hours}:00`
    },
    getOffsetMinutes (timeZone = 'UTC', date = new Date()) {
      const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
      const tzDate = new Date(date.toLocaleString('en-US', { timeZone }))

      return (tzDate.getTime() - utcDate.getTime()) / 60 / 1000
    }
  }
}

</script>

<style lang="scss" scoped>
  .poster-img {
    width: 60px;
    height: 80px;
  }
  .banner-img {
    width: 100%;
  }
  .toggle-title {
    justify-content: space-between;
  }
</style>
